import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { InfiniteQueryObserverResult } from '@tanstack/react-query';
import {
  useDeleteCommentLikeMutation,
  useDeleteCommentMutation,
  useDeleteGameCommentLikeMutation,
  useDeleteGameCommentMutation,
  useSaveCommentLikeMutation,
  useSaveGameCommentLikeMutation,
} from '~/features/comment';
import { useIntersectionObserver, useModal } from '~/hooks';
import { DeleteCommentRequest, GetCommentData, ModalParams } from '~/types';
import { DeleteGameCommentRequest } from '~/types/missionGameApi';
import { useUserStore } from '~/store';
import Comment from './Comment';
import { SortComment } from './CommentInner';

export interface CommentListProps {
  allList: {
    pages: GetCommentData[];
    pageParams: number[];
    total: number;
  };
  fetchNextPage: () => Promise<InfiniteQueryObserverResult>;
  isFetchingNextPage?: boolean;
  editingComment: GetCommentData | null;
  setEditingComment: React.Dispatch<
    React.SetStateAction<GetCommentData | null>
  >;
  sortOrder: SortComment;
  isMissionGame?: boolean;
}

export default function CommentList({
  allList,
  fetchNextPage,
  isFetchingNextPage,
  editingComment,
  setEditingComment,
  sortOrder,
  isMissionGame,
}: CommentListProps) {
  const email = useUserStore((state) => state.email);
  const { id } = useParams<{ id: string }>();
  const target_id = id || '';
  const { openModal } = useModal();
  const { state } = useLocation();

  const [openCommentId, setOpenCommentId] = useState<number | null>(null);

  const { isIntersecting, ref } = useIntersectionObserver({ threshold: 0.1 });

  const { mutate: deleteCommentMutate, isPending: deleteCommentPending } =
    useDeleteCommentMutation(state?.type || 'mission');
  const {
    mutate: deleteGameCommentMutate,
    isPending: deleteGameCommentPending,
  } = useDeleteGameCommentMutation();

  const {
    mutate: deleteCommentLikeMutate,
    isPending: deleteCommentLikePending,
  } = useDeleteCommentLikeMutation();
  const {
    mutate: deleteGameCommentLikeMutate,
    isPending: deleteGameCommentLikePending,
  } = useDeleteGameCommentLikeMutation();

  const { mutate: saveCommentLikeMutate, isPending: saveCommentLikePending } =
    useSaveCommentLikeMutation();
  const {
    mutate: saveGameCommentLikeMutate,
    isPending: saveGameCommentLikePending,
  } = useSaveGameCommentLikeMutation();

  useEffect(() => {
    if (isIntersecting) {
      fetchNextPage();
    }
  }, [isIntersecting]);

  const toggleSubMenu = (commentId: number) => {
    setOpenCommentId((prev) => (prev === commentId ? null : commentId));
  };

  const handleDeleteComment = async (comment_id: number) => {
    try {
      const requestData = isMissionGame
        ? ({
            comment_id,
            email,
            game_id: Number(target_id),
          } as DeleteGameCommentRequest)
        : ({
            comment_id,
            email,
            character_id: target_id,
          } as DeleteCommentRequest);

      isMissionGame
        ? deleteGameCommentMutate(requestData as DeleteGameCommentRequest, {
            onSuccess({ status }) {
              setOpenCommentId(null);
            },
          })
        : deleteCommentMutate(requestData as DeleteCommentRequest, {
            onSuccess({ status }) {
              setOpenCommentId(null);
            },
          });
    } catch (error) {
      console.error(error);
    }
  };

  const handleEditComment = (data: GetCommentData) => {
    setOpenCommentId(null);
    setEditingComment(data);
  };

  const handleCommentLikeClick = (comment_id: number, is_liked: boolean) => {
    if (!isMissionGame && (saveCommentLikePending || deleteCommentLikePending))
      return;
    if (
      isMissionGame &&
      (saveGameCommentLikePending || deleteGameCommentLikePending)
    )
      return;

    if (is_liked) {
      isMissionGame
        ? deleteGameCommentLikeMutate({
            comment_id,
            game_id: Number(target_id),
          })
        : deleteCommentLikeMutate({
            comment_id,
            character_id: target_id,
          });
    } else {
      isMissionGame
        ? saveGameCommentLikeMutate({
            comment_id,
            game_id: Number(target_id),
          })
        : saveCommentLikeMutate({ comment_id, character_id: target_id });
    }
  };

  const handleDeleteModal = (comment_id: number) => {
    const modalDeleteRequest: ModalParams = {
      title: 'Delete comment',
      content: 'Are you sure you want to delete comment?',
      click: () => handleDeleteComment(comment_id),
      buttonName: 'Delete',
    };

    openModal(modalDeleteRequest);
  };

  return (
    <Container>
      {allList.pages.map((data, idx) => (
        <Comment
          data={data}
          key={`${data.comment_id}_${data.writer}_${idx}`}
          isSubMenuOpen={openCommentId === data.comment_id}
          toggleSubMenu={() => toggleSubMenu(data.comment_id)}
          handleDeleteComment={() => handleDeleteModal(data.comment_id)}
          handleEditComment={() => handleEditComment(data)}
          handleCommentLikeClick={handleCommentLikeClick}
          setOpenCommentId={setOpenCommentId}
        />
      ))}

      {!isFetchingNextPage && <div ref={ref} />}
    </Container>
  );
}

export const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 16px 0 64px;
    overflow-y: scroll;
    gap: 32px;
    ${theme.mixins.scroll.none};
  `}
`;
